<template>
  <div class="mt-2 mb-2">
    <span
      v-if="store.isAvailable"
      class="badge badge-pill badge-success badge-padding"
    >
      {{ $t('lang.stores.open') }}
      <span v-if="scheduleForSearchDate === '24 hs'">
        {{ scheduleForSearchDate }}
      </span>
    </span>

    <div v-else>
      <span
        class="badge badge-pill badge-danger badge-padding"
      >{{ closedBadge }}</span>
      <small
        v-if="origin === 'details'"
        class="ml-1"
      >
        {{ $t('lang.stores.reason_closed.'+ reasonClosedId ) +
          $t('lang.stores.reason_closed.try_again') }}
      </small>
    </div>

    <span
      v-if="store.isAvailable && scheduleForSearchDate !== '24 hs'"
      class=" small"
    >
      {{ scheduleForSearchDate }}
    </span>
  </div>
</template>

<script>

export default {
  name: 'ScheduleBadge',
  components: { },
  props: {
    store: {
      type: Object,
      required: true,
    },
    searchParams: {
      type: Object,
      required: false,
    },
    origin: {
      type: String,
      default: 'card',
    },
  },

  computed: {
    closedBadge() {
      return this.storeClosedMessage();
    },
    reasonClosedId() {
      const reasons = Object.values(this.store.reason_closed);
      // In order of importance
      if (reasons.includes('holiday')) {
        return 'holiday';
      }
      if (reasons.includes('capacity')) {
        return 'capacity';
      }
      if (reasons.includes('currency')) {
        return 'currency';
      }

      return 'schedule';
    },

    scheduleForSearchDate() {
      if (!this.store.availabilities || !this.store.availabilities.length) return '';
      if (!this.searchParams || !this.searchParams.input_start_date) return '';
      const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
      const searchDate = new Date(this.searchParams.input_start_date);
      const searchDayName = days[searchDate.getDay()];

      const dayAvailability = this.store.availabilities.find(
        (avail) => avail.day === searchDayName,
      );
      if (!dayAvailability || !dayAvailability.schedules || !dayAvailability.schedules.length) return '';

      const firstSchedule = dayAvailability.schedules[0];
      if (!firstSchedule.open_hour || !firstSchedule.close_hour) return '';

      // Format the time using the same method as in StoreSchedule
      const formatTime = (timeStr) => {
        const [hours, minutes] = timeStr.split(':');
        // Convert to number to remove leading zero and add minutes if needed
        const formattedHours = parseInt(hours, 10);
        const formattedMinutes = minutes !== '00' ? `:${minutes}` : '';
        return `${formattedHours}${formattedMinutes}`;
      };

      if (firstSchedule.open_hour === '00:00:00' && firstSchedule.close_hour === '23:59:00') {
        return '24 hs';
      }

      return `${this.$t('lang.stores.schedule_texts.from')} ${
        formatTime(firstSchedule.open_hour)} ${
        this.$t('lang.stores.schedule_texts.to')} ${
        formatTime(firstSchedule.close_hour)} hs`;
    },
  },
  methods: {
    reasonClosedStore() {
      const reasonClosed = {
        currency: false,
        capacity: false,
        holiday: false,
      };

      this.store.reason_closed.forEach((reason) => {
        if (reason === 'currency') {
          reasonClosed.currency = true;
        }
        if (reason === 'capacity') {
          reasonClosed.capacity = true;
        }
        if (reason === 'holiday') {
          reasonClosed.holiday = true;
        }
      });
      return reasonClosed;
    },

    storeClosedMessage() {
      const reasonClosed = this.reasonClosedStore();
      // In order of importance
      if (reasonClosed.holiday) {
        return this.$t('lang.stores.close');
      }
      if (reasonClosed.capacity) {
        return this.$t('lang.stores.capacity_close');
      }
      if (reasonClosed.currency) {
        return this.$t('lang.stores.currency_close') + this.$store.state.main.currency;
      }

      // continue for schedule errors
      let message = null;
      const weekDayString = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

      const searchStartDate = new Date(this.searchParams.input_start_date);
      const searchStartDayOfWeek = weekDayString[searchStartDate.getDay()];
      const searchStartHour = `${searchStartDate.getHours().toString().padStart(2, '0')}:${searchStartDate.getMinutes().toString().padStart(2, '0')}:00`;

      const searchEndDate = new Date(this.searchParams.input_end_date);
      const searchEndDayOfWeek = weekDayString[searchEndDate.getDay()];
      const searchEndHour = `${searchEndDate.getHours().toString().padStart(2, '0')}:${searchEndDate.getMinutes().toString().padStart(2, '0')}:00`;

      let storeStartDaySchedule = null;
      let storeEndDaySchedule = null;

      let openAtStartHour = false;
      let openAtEndHour = false;

      // assign start and end date schedules
      this.store.availabilities.forEach((availability) => {
        if (availability.day === searchStartDayOfWeek) {
          storeStartDaySchedule = availability.schedules;
        }
        if (availability.day === searchEndDayOfWeek) {
          storeEndDaySchedule = availability.schedules;
        }
      });

      if (!storeStartDaySchedule || !storeEndDaySchedule) {
        return this.$t('lang.stores.close');
      }

      // evaluate if problem is with start or end date.
      storeStartDaySchedule.forEach((schedule) => {
        const storeOpenHour = schedule.open_hour;
        const storeCloseHour = schedule.close_hour;

        if (storeOpenHour <= searchStartHour && searchStartHour <= storeCloseHour) {
          openAtStartHour = true;
        }
      });

      storeEndDaySchedule.forEach((schedule) => {
        const storeOpenHour = schedule.open_hour;
        const storeCloseHour = schedule.close_hour;

        if (storeOpenHour <= searchEndHour && searchEndHour <= storeCloseHour) {
          openAtEndHour = true;
        }
      });

      // evaluate start date problems
      if (!openAtStartHour) {
        let startStoreOpenHour = null;
        let startStoreCloseHour = null;

        // Evaluate if the store opens later
        storeStartDaySchedule.forEach((schedule) => {
          startStoreOpenHour = schedule.open_hour;
          startStoreCloseHour = schedule.close_hour;
          if (searchStartHour < startStoreOpenHour && message === null) {
            message = this.$t('lang.stores.opens_at') + startStoreOpenHour;
          }
        });

        // If the store closed earlier
        if (message === null) {
          message = this.$t('lang.stores.closes_at') + startStoreCloseHour;
        }
      }

      // evaluate end date problems
      if (!openAtEndHour && message === null) {
        let endStoreOpenHour = null;
        let endStoreCloseHour = null;

        // Evaluate if the store closed earlier
        storeEndDaySchedule.forEach((schedule) => {
          endStoreCloseHour = schedule.close_hour;

          // Here we don't check if message is still null -> if the next schedule of day is true,
          // then that hour is the closest to the searchEndHour
          if (searchEndHour > endStoreCloseHour) {
            message = this.$t('lang.stores.closes_at') + endStoreCloseHour;
          }

          // only save the first open hour of the store in case the store opens later
          if (!endStoreOpenHour) {
            endStoreOpenHour = schedule.open_hour;
          }
        });

        // If the store opens later
        if (message === null) {
          message = this.$t('lang.stores.opens_at') + endStoreOpenHour;
        }
      }

      if (!message) {
        message = this.$t('lang.stores.close');
      } else {
        message = message.slice(0, -3);
        message += ' hs';
      }

      return message;
    },

  },
};
</script>

<style scoped>

</style>
